import { useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, Tooltip, CircularProgress, ToggleButtonGroup, ToggleButton,
  Alert, Tabs, Tab,
} from '@mui/material';
import {
  Print as PrintIcon, Description as WordIcon, TableChart as ExcelIcon,
  Close as CloseIcon, ViewAgenda as CombinedIcon,
  ViewList as PerAccountIcon,
} from '@mui/icons-material';

// Receipt rendering is done as plain HTML with inline styles (no MUI sx).
// That's intentional: the print window only sees the serialised innerHTML,
// so any emotion-generated class names would have no matching stylesheet
// and the print output would lose every dimension and border. With inline
// style attributes, what you see in the dialog is byte-identical to what
// comes out of the printer.

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

// ── Shared styles for the rendered receipt (A5 landscape on A4) ──
// We print on A4 portrait paper so the receipt fills the TOP HALF of
// the sheet (210mm x 148mm = A5 landscape), and the user folds the
// page horizontally to get a half-A4 receipt.
const RECEIPT_CSS = `
  @page { size: A4 portrait; margin: 0; }
  .receipt-sheet {
    position: relative;
    width: 210mm;
    height: 148mm;
    background: #ffffff;
    color: #000;
    font-family: 'Times New Roman', Georgia, serif;
    padding: 6mm 10mm;
    margin: 0 auto;
    overflow: hidden;
    box-sizing: border-box;
    font-size: 10px;
    line-height: 1.3;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt-watermark {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 50%;
    max-width: 95mm;
    max-height: 95mm;
    object-fit: contain;
    opacity: 0.06;
    pointer-events: none;
    z-index: 0;
    user-select: none;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt-watermark-text {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Times New Roman', Georgia, serif;
    font-size: 44px;
    font-weight: 800;
    color: #111;
    opacity: 0.04;
    text-transform: uppercase;
    letter-spacing: 2px;
    text-align: center;
    width: 180mm;
    line-height: 1.05;
    pointer-events: none;
    z-index: 0;
    user-select: none;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt-content { position: relative; z-index: 1; }
  .receipt-toplabel {
    display: table; width: 100%;
    font-size: 8.5px; margin-bottom: 2px;
  }
  .receipt-toplabel > div { display: table-cell; }
  .receipt-toplabel .right { text-align: right; }
  .receipt-header { display: table; width: 100%; margin-bottom: 4px; }
  .receipt-header > div { display: table-cell; vertical-align: middle; }
  .receipt-logo { width: 44px; height: 44px; object-fit: contain; display: block; }
  .receipt-school-name {
    font-weight: 800; font-size: 16px;
    text-decoration: underline; text-transform: uppercase;
    letter-spacing: 0.4px; text-align: center;
  }
  .receipt-info { width: 100%; border-collapse: collapse; font-size: 9px; margin-bottom: 4px; }
  .receipt-info td { padding: 1px 4px; vertical-align: top; }
  .receipt-info .lbl { font-weight: 700; white-space: nowrap; }
  .receipt-divider { border: none; border-top: 1px solid #000; margin: 4px 0 5px; }
  .receipt-title {
    text-align: center; font-weight: 800; font-size: 13px;
    text-decoration: underline; margin: 5px 0 6px;
  }
  .receipt-date { font-size: 9.5px; margin-bottom: 4px; }
  .receipt-date b { font-weight: 700; }
  .receipt-section { margin-bottom: 6px; font-size: 9.5px; }
  .receipt-section-h {
    font-weight: 800; font-size: 10px; margin-bottom: 3px;
    text-transform: uppercase; letter-spacing: 0.3px;
  }
  .receipt-stack > div { padding: 1px 0; }
  .receipt-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 14px; }
  .receipt-cell b { font-weight: 700; }
  .receipt-balance {
    margin-top: 4px; padding-top: 3px; border-top: 1px solid #000;
    font-weight: 700; font-size: 10px;
  }
  .receipt-twocol {
    display: grid;
    grid-template-columns: 1.05fr 1fr;
    gap: 6px 12px;
    margin-bottom: 6px;
    font-size: 9.5px;
  }
  .receipt-twocol .col { min-width: 0; }
  .receipt-table { width: 100%; border-collapse: collapse; font-size: 8.5px; margin-top: 2px; }
  .receipt-table th {
    border: 1px solid #000; padding: 3px 5px; background: #efefef;
    text-align: left; font-weight: 700;
  }
  .receipt-table td { border: 1px solid #000; padding: 3px 5px; }
  .receipt-table .right { text-align: right; }
  .receipt-table tr.current { background: #fff8e1; }
  .receipt-history-h {
    text-align: center; font-weight: 800; font-size: 10px;
    margin: 7px 0 3px; text-transform: uppercase;
  }
  .receipt-signature { margin-top: 6mm; font-size: 9px; }
  .receipt-signature .line {
    width: 55mm; border-top: 1px dotted #000; padding-top: 3px;
    font-weight: 700;
  }
  .page-break { page-break-after: always; break-after: page; }

  /* ── Print-only refinements ───────────────────────────────────
     The on-screen 10px base reads ~7.5pt on paper, which prints
     too small to scan at arm's length. We bump to a pt-based
     scale here so values are stable across browser/printer combos
     and ensure html + body have no implicit margin so the A5
     sheet snaps to the corner of the page (no "letter-in-a-page"
     shrunken effect when the OS preview centers small content).  */
  @media print {
    html, body { margin: 0; padding: 0; background: #ffffff; }
    .receipt-sheet {
      margin: 0 auto; box-shadow: none;
      width: 210mm; height: 148mm; padding: 6mm 10mm;
      font-size: 10pt; line-height: 1.3;
      page-break-after: always; break-after: page;
    }
    .receipt-watermark      { opacity: 0.07; }
    .receipt-watermark-text { opacity: 0.05; font-size: 48pt; }

    .receipt-toplabel       { font-size: 8pt; }
    .receipt-info           { font-size: 9pt; }
    .receipt-school-name    { font-size: 16pt; letter-spacing: 0.5pt; }
    .receipt-title          { font-size: 12pt; }
    .receipt-date           { font-size: 9.5pt; }
    .receipt-section,
    .receipt-twocol         { font-size: 9.5pt; }
    .receipt-section-h,
    .receipt-history-h,
    .receipt-balance        { font-size: 9.5pt; }
    .receipt-table          { font-size: 8.5pt; }
    .receipt-table th,
    .receipt-table td       { padding: 3px 5px; }
    .receipt-signature      { font-size: 9pt; margin-top: 5mm; }
  }
`;

// ── HTML builders ─────────────────────────────────────────────
function headerHTML(data, topLabel) {
  const s = data.school || {};
  const stu = data.student || {};
  const pay = data.payment || {};
  const fullName = `${stu.firstname || ''} ${stu.lastname || ''}`.trim().toUpperCase();
  const logo = s.logo
    ? `<img class="receipt-logo" src="${esc(s.logo)}" alt="logo" />`
    : '';
  const spacer = s.logo ? '<div style="width:44px;"></div>' : '';
  return `
    <div class="receipt-toplabel">
      <div>${esc(topLabel || 'School Fee Payment Receipt')}</div>
      <div class="right">${esc(s.website || '')}</div>
    </div>

    <div class="receipt-header">
      <div style="width:44px;">${logo}</div>
      <div><div class="receipt-school-name">${esc(s.school_name || 'School Name')}</div></div>
      <div style="text-align:right;">${spacer}</div>
    </div>

    <table class="receipt-info">
      <tr>
        <td class="lbl">Address:</td><td>${esc(s.address || '—')}</td>
        <td class="lbl" style="text-align:right;">Tele:</td>
        <td style="text-align:right;">${esc(s.phone || '—')}</td>
      </tr>
      <tr>
        <td class="lbl">Location:</td><td>${esc(s.region || '—')}</td>
        <td class="lbl" style="text-align:right;">Email:</td>
        <td style="text-align:right;">${esc(s.email || '—')}</td>
      </tr>
      <tr>
        <td class="lbl">Mission:</td><td>${esc(s.motto || '—')}</td>
        <td class="lbl" style="text-align:right;">Website:</td>
        <td style="text-align:right;">${esc(s.website || '—')}</td>
      </tr>
    </table>

    <hr class="receipt-divider" />
  `;
}

function studentInfoColHTML(data) {
  const stu = data.student || {};
  const pay = data.payment || {};
  const fullName = `${stu.firstname || ''} ${stu.lastname || ''}`.trim().toUpperCase();
  return `
    <div class="receipt-date" style="margin-bottom:3px;"><b>Date:</b> ${esc(fmtDate(pay.pay_date))}</div>
    <div class="receipt-section-h">Student Information</div>
    <div class="receipt-grid-2" style="gap:2px 8px;">
      <div><b>Index Number:</b> ${esc(stu.index_no || '—')}</div>
      <div><b>Academic Period:</b> ${esc(pay.acc_yer || '—')}</div>
      <div><b>Name:</b> ${esc(fullName || '—')}</div>
      <div><b>Term:</b> ${esc(pay.term || '—')}</div>
      <div><b>Class:</b> ${esc(stu.calss || '—')}</div>
    </div>
  `;
}

function combinedBodyHTML(data) {
  const sum = data.summary || {};
  const hist = data.history || [];
  const pay = data.payment || {};
  const histRows = hist.length === 0
    ? `<tr><td colspan="6" style="text-align:center;color:#666;">No prior payments</td></tr>`
    : hist.map(h => {
        const cls = h.payment_id === pay.payment_id ? 'current' : '';
        return `
          <tr class="${cls}">
            <td>${esc(fmtDate(h.pay_date))}</td>
            <td>${esc(h.mon_yer)}</td>
            <td>${esc(h.receipt_no)}</td>
            <td>${esc(h.bank_account || h.mode)}</td>
            <td class="right">GHS ${fmtMoney(h.total_amount)}</td>
            <td>${esc(h.received_by_name || '—')}</td>
          </tr>`;
      }).join('');
  return `
    <div class="receipt-title">Fee Payment Receipt</div>

    <div class="receipt-twocol">
      <div class="col">${studentInfoColHTML(data)}</div>
      <div class="col">
        <div class="receipt-section-h">Summary of Fees Account</div>
        <div class="receipt-stack">
          <div><b>Arrears:</b> GHS ${fmtMoney(sum.arrears)}</div>
          <div><b>Term Fee:</b> GHS ${fmtMoney(sum.term_bill)}</div>
          ${Number(sum.add_bill) > 0
            ? `<div><b>Added Bill:</b> GHS ${fmtMoney(sum.add_bill)}</div>`
            : ''}
          ${Number(sum.scholarship_amount) > 0
            ? `<div><b>Scholarship:</b> -GHS ${fmtMoney(sum.scholarship_amount)}</div>`
            : ''}
          ${Number(sum.write_off) > 0
            ? `<div><b>Write-off:</b> -GHS ${fmtMoney(sum.write_off)}</div>`
            : ''}
          <div><b>Total Bill:</b> GHS ${fmtMoney(sum.total_bill)}</div>
          <div><b>Total Paid:</b> GHS ${fmtMoney(sum.total_paid)}</div>
        </div>
        <div class="receipt-balance">
          <b>Balance Left:</b> GHS ${fmtMoney(sum.balance_left)}
        </div>
      </div>
    </div>

    <div class="receipt-history-h">
      Payment History${data.history_scope?.scoped
        ? ` <span style="font-weight:600;font-size:9px;color:#475569">— ${esc(data.history_scope.term || '')}${data.history_scope.acc_yer ? ` · ${esc(data.history_scope.acc_yer)}` : ''}</span>`
        : ''}
    </div>
    <table class="receipt-table">
      <thead><tr>
        <th>Date</th><th>Month/year</th><th>Receipt NO.</th><th>Mode</th>
        <th class="right">Amount Paid</th><th>Received By</th>
      </tr></thead>
      <tbody>${histRows}</tbody>
    </table>
  `;
}

function singleAccountBodyHTML(data, line) {
  const accounts = data.accounts || [];
  const pay = data.payment || {};
  const acc = accounts.find(a => a.account === line.acc_name) || {};
  const prev = Number(line.previous_balance || 0);
  const after = Number(line.new_balance || 0);
  const paidThis = Number(line.amount || 0);
  const totalPaidNow = Number(acc.t_paid || 0);
  // Break adjustments into two distinct lines so it's obvious which way
  // each one moves the bill: Added Bill = add_bal (raises), Write-off =
  // wri (reduces). Plus the Scholarship % (always shown if non-zero).
  const addedBill   = Number(acc.add_bal || 0);
  const writeOff    = Number(acc.wri || 0);
  const scholarship = Number(acc.scholarship_amount || 0);
  const schoPct     = Number(acc.scholarship_pct || 0);

  return `
    <div class="receipt-title">${esc(line.acc_name)} Payment Receipt</div>

    <div class="receipt-twocol">
      <div class="col">${studentInfoColHTML(data)}</div>
      <div class="col">
        <div class="receipt-section-h">Account: ${esc(line.acc_name)}</div>
        <div class="receipt-grid-2">
          <div class="receipt-cell"><b>Term Bill:</b> GHS ${fmtMoney(acc.tobe)}</div>
          <div class="receipt-cell"><b>Arrears:</b> GHS ${fmtMoney(acc.arr)}</div>
          ${addedBill > 0
            ? `<div class="receipt-cell"><b>Added Bill:</b> +GHS ${fmtMoney(addedBill)}</div>`
            : ''}
          ${scholarship > 0
            ? `<div class="receipt-cell"><b>Scholarship${schoPct ? ` (${schoPct}%)` : ''}:</b> -GHS ${fmtMoney(scholarship)}</div>`
            : ''}
          ${writeOff > 0
            ? `<div class="receipt-cell"><b>Write-off:</b> -GHS ${fmtMoney(writeOff)}</div>`
            : ''}
          <div class="receipt-cell"><b>Total Due:</b> GHS ${fmtMoney(acc.total_due)}</div>
          <div class="receipt-cell"><b>Total Paid:</b> GHS ${fmtMoney(totalPaidNow)}</div>
        </div>
        <div class="receipt-balance">
          ${Number(acc.balance) < -0.01
            ? `<b>Credit Balance:</b> GHS ${fmtMoney(Math.abs(acc.balance))}`
            : `<b>Balance Left:</b> GHS ${fmtMoney(acc.balance)}`}
        </div>
      </div>
    </div>

    <div class="receipt-history-h">This Payment</div>
    <table class="receipt-table">
      <thead><tr>
        <th>Date</th><th>Month/year</th><th>Receipt NO.</th><th>Mode</th>
        <th class="right">Prev Bal.</th>
        <th class="right">Amount Paid</th>
        <th class="right">New Bal.</th>
        <th>Received By</th>
      </tr></thead>
      <tbody>
        <tr class="current">
          <td>${esc(fmtDate(pay.pay_date))}</td>
          <td>${esc(pay.mon_yer)}</td>
          <td>${esc(pay.receipt_no)}</td>
          <td>${esc(pay.bank_account || pay.mode)}</td>
          <td class="right">GHS ${fmtMoney(prev)}</td>
          <td class="right" style="font-weight:800;">GHS ${fmtMoney(paidThis)}</td>
          <td class="right">GHS ${fmtMoney(after)}</td>
          <td>${esc(pay.received_by_name || '—')}</td>
        </tr>
      </tbody>
    </table>
  `;
}

function sheetHTML(data, bodyInner) {
  const s = data.school || {};
  const watermark = s.logo
    ? `<img class="receipt-watermark" src="${esc(s.logo)}" alt="" />`
    : `<div class="receipt-watermark-text">${esc(s.school_name || 'Receipt')}</div>`;
  return `
    <div class="receipt-sheet">
      ${watermark}
      <div class="receipt-content">
        ${headerHTML(data)}
        ${bodyInner}
        <div class="receipt-signature">
          <div class="line">Accountant Signature</div>
        </div>
      </div>
    </div>
  `;
}

// Exported so the parent portal renders the SAME receipt the cashier
// prints, rather than a lookalike that drifts the first time the school
// header or the fee summary changes. The portal passes the same `data`
// shape, assembled by /api/portal/receipts.
export function combinedHTML(data) {
  return sheetHTML(data, combinedBodyHTML(data));
}
export { RECEIPT_CSS };

function singleAccountHTML(data, lineIdx) {
  const line = data.lines?.[lineIdx];
  if (!line) return '';
  return sheetHTML(data, singleAccountBodyHTML(data, line));
}

function allPerAccountHTML(data) {
  const lines = data.lines || [];
  return lines.map((line, i) => {
    const sheet = sheetHTML(data, singleAccountBodyHTML(data, line));
    if (i < lines.length - 1) {
      return sheet + '<div class="page-break"></div>';
    }
    return sheet;
  }).join('');
}

function fullDocument(innerHTML, title) {
  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>${RECEIPT_CSS}</style>
  </head><body>${innerHTML}</body></html>`;
}

// ── Helpers for export ────────────────────────────────────────
function downloadBlob(content, mime, filename) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Component ─────────────────────────────────────────────────
export default function ReceiptDialog({ open, onClose, data, loading, error }) {
  const [mode, setMode]   = useState('combined');
  const [tabIdx, setTabIdx] = useState(0);

  const lines = data?.lines || [];
  const hasLines = lines.length > 0;
  const receiptNo = data?.payment?.receipt_no || 'receipt';

  // Reset to first tab when switching modes or data changes
  const onModeChange = (_, v) => {
    if (!v) return;
    setMode(v);
    setTabIdx(0);
  };

  // Visible HTML (single sheet — combined or one account)
  const visibleHTML = !data ? ''
    : mode === 'per-account'
      ? singleAccountHTML(data, tabIdx)
      : combinedHTML(data);

  // Print HTML — for per-account mode, prints ALL accounts with page breaks
  const handlePrint = () => {
    if (!data) return;
    const inner = mode === 'per-account'
      ? allPerAccountHTML(data)
      : combinedHTML(data);
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) return;
    w.document.write(fullDocument(inner, `Receipt ${receiptNo}`));
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 350);
  };

  const handleExportWord = () => {
    if (!data) return;
    const inner = mode === 'per-account'
      ? allPerAccountHTML(data)
      : combinedHTML(data);
    downloadBlob(fullDocument(inner, `Receipt ${receiptNo}`),
      'application/msword', `Receipt-${receiptNo}.doc`);
  };

  const handleExportExcel = () => {
    if (!data) return;
    const s = data.school || {};
    const stu = data.student || {};
    const pay = data.payment || {};
    const sum = data.summary || {};
    const hist = data.history || [];
    const fullName = `${stu.firstname || ''} ${stu.lastname || ''}`.trim();
    const rows = [];
    rows.push(['School', s.school_name || '']);
    rows.push(['Receipt', pay.receipt_no || '']);
    rows.push(['Date', pay.pay_date || '']);
    rows.push([]);
    rows.push(['Student', fullName, 'Index', stu.index_no || '', 'Class', stu.calss || '']);
    rows.push(['Term', pay.term || '', 'Year', pay.acc_yer || '']);
    rows.push([]);
    rows.push(['Arrears', sum.arrears, 'Term Fee', sum.term_bill]);
    rows.push(['Total Bill', sum.total_bill, 'Total Paid', sum.total_paid]);
    rows.push(['Balance Left', sum.balance_left]);
    rows.push([]);
    rows.push(['Date', 'Month/year', 'Receipt NO.', 'Mode', 'Amount Paid', 'Received By']);
    for (const h of hist) {
      rows.push([
        h.pay_date, h.mon_yer, h.receipt_no,
        h.bank_account || h.mode, h.total_amount, h.received_by_name || '',
      ]);
    }
    const escapeCsv = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map(r => r.map(escapeCsv).join(',')).join('\n');
    downloadBlob(csv, 'text/csv;charset=utf-8', `Receipt-${receiptNo}.csv`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: '#eef0f4' }}>
        {loading ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">Loading receipt…</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : data ? (
          <Box sx={{ py: 3 }}>
            {/* Mode toggle */}
            <Box sx={{ maxWidth: 860, mx: 'auto', px: { xs: 2, sm: 3 }, mb: 1 }}>
              <ToggleButtonGroup
                size="small" exclusive value={mode} onChange={onModeChange}
                sx={{ bgcolor: '#fff' }}
              >
                <ToggleButton value="combined" sx={{ textTransform: 'none', fontWeight: 700 }}>
                  <CombinedIcon fontSize="small" sx={{ mr: 0.6 }} />
                  Combined
                </ToggleButton>
                <ToggleButton value="per-account" disabled={!hasLines}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  <PerAccountIcon fontSize="small" sx={{ mr: 0.6 }} />
                  Per Account ({lines.length})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Per-account tabs — pick which receipt to view */}
            {mode === 'per-account' && lines.length > 0 && (
              <Box sx={{
                maxWidth: 860, mx: 'auto', px: { xs: 2, sm: 3 }, mb: 1,
              }}>
                <Box sx={{ bgcolor: '#fff', borderRadius: 1, boxShadow: 1 }}>
                  <Tabs
                    value={tabIdx}
                    onChange={(_, v) => setTabIdx(v)}
                    variant="scrollable" scrollButtons="auto"
                    sx={{ minHeight: 40,
                      '& .MuiTab-root': { minHeight: 40, fontWeight: 700, textTransform: 'none' } }}
                  >
                    {lines.map((line, i) => (
                      <Tab key={line.line_id || i}
                        label={
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                              {line.acc_name}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                              GHS {fmtMoney(line.amount)}
                            </Typography>
                          </Box>
                        } />
                    ))}
                  </Tabs>
                </Box>
              </Box>
            )}

            {/* The receipt sheet itself — same HTML used for view and print */}
            <Box sx={{ maxWidth: 860, mx: 'auto', px: { xs: 1, sm: 2 } }}>
              <Box sx={{
                bgcolor: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
              }}>
                <style>{RECEIPT_CSS}</style>
                <div dangerouslySetInnerHTML={{ __html: visibleHTML }} />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Could not load receipt.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderTop: '1px solid', borderColor: 'divider' }}>
        <Tooltip title="Close">
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleExportExcel} startIcon={<ExcelIcon />}
          variant="outlined" disabled={!data}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Excel
        </Button>
        <Button onClick={handleExportWord} startIcon={<WordIcon />}
          variant="outlined" disabled={!data}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Word
        </Button>
        <Button onClick={handlePrint} startIcon={<PrintIcon />}
          variant="contained" disabled={!data}
          sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
          Print {mode === 'per-account' && lines.length > 1 ? `All ${lines.length}` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
